import {
  ALLOWED_ARNM_AGENT_ROLES,
  ARNM_REASONS_MAP,
  BOARDS_ID,
  FILTER_OPERATOR_ENUMS,
  FILTER_RULE_ENUMS,
} from '@constants';
import { formatDate, sortArray } from '@utils';
import {
  ARNM_MAINTENANCE_TASK_STATUSES_NAMES,
  ARNM_MAINTENANCE_TASK_STATUSES_ORDER,
  ARNM_REASONS_MAINTENANCE_MAP,
} from 'components/arnmMaintenance/constants';
import { URLS } from 'layouts/DrawerLayout/constants';
import { groupBy, omit } from 'lodash-es';

const getValue = (data, current) => {
  return data.find((item) => item?.value === current);
};

export const handleArnmMaintenanceTaskData = (
  data,
  servicePoints = [],
  assignees = [],
  stages = []
) => {
  if (!data) {
    return {};
  }
  const servicePoint = getValue(servicePoints, data.customFields.servicePoint);
  const assignee = getValue(assignees, data.assignee);
  const status = getValue(stages, data.status);

  const task = {
    ...data,
    assignee,
    status,
    customFields: {
      ...omit(data.customFields, ['comments']),
      reason: ARNM_REASONS_MAINTENANCE_MAP[data.customFields?.reason] || '-',
      servicePoint,
      totalChargeClientAmount:
        data.customFields.totalChargeClientAmount?.number || null,
      totalOfferAmount: data.customFields.totalOfferAmount?.number || null,
      totalInvoiceCost: data.customFields.totalInvoiceCost?.number || null,
      workOrder: data.customFields.workOrder?.map?.((item) => ({
        ...item,
        chargeClientAmount: item.chargeClientAmount?.number || null,
        offerAmount: item.offerAmount?.number || null,
      })),
    },
    changelog: data?.changelog?.reverse(),
    comments: sortArray(data?.comments, 'createdAt', 'desc')?.map((entry) => {
      return {
        id: entry?.id,
        date: entry?.createdAt
          ? formatDate(entry?.createdAt, 'dd MMMM yyyy')
          : '-',
        time: entry?.createdAt ? formatDate(entry?.createdAt, 'HH:mm') : '-',
        name: entry?.userFullName || entry?.user || '-',
        comment: entry?.comment,
        edit: entry?.editedAt
          ? formatDate(entry?.editedAt, 'dd MMMM yyyy HH:mm')
          : '',
      };
    }),
    documents: data?.documents,
  };
  return task;
};

export const handleVehicleData = (data = {}) => {
  const properties = [];

  if (data.properties?.fuelType) {
    properties.push(data.properties.fuelType);
  }
  if (data.properties?.transmission) {
    properties.push(data.properties.transmission);
  }
  if (data.properties?.color) {
    properties.push(data.properties.color);
  }
  if (data.year) {
    properties.push(data.year);
  }

  return {
    internalId: data.internalId,
    id: data.id,
    title: `${data.manufacturer}, ${data.model}`,
    plate: data.plate,
    image: data?.images?.[0]?.url,
    properties,
    vin: data.vin,
    procurement: data.procurement,
  };
};

export const handleArnmAssigneeData = (data = []) =>
  data?.filter((item) => ALLOWED_ARNM_AGENT_ROLES.has(item?.role));

export const handleArnmMaintenanceData = (data = [], assignees = []) => {
  if (!data.length) {
    return [];
  }
  const assigneesMap = assignees.reduce((acc, cur) => {
    acc[cur.value] = cur;
    return acc;
  }, {});

  const tasks = data.map((item) => {
    const assignee = assigneesMap[item.assignee] || {};

    const vehicleDescription = item?.customFields?.vehicleDescription || '';

    const vehicleReason = item?.customFields?.reason || '';

    const description = `${vehicleDescription} - ${vehicleReason}`;

    return {
      ...item,
      href: `${URLS.arnmMaintenance}/${item?.id}`,
      plate: item.customFields?.vehiclePlate || '',
      status: item?.status,
      assignee,
      description,
      createdAt: item?.createdAt
        ? formatDate(item?.createdAt, 'dd/MM/yyyy')
        : '-',
      date: item.createdAt ? formatDate(item.createdAt, 'dd/MM/yyyy') : null,
      // TODO: handle custom fields
      id: item?.id,
      linkColor: item?.customFields?.doWeNeedATempCar && 'red',
      servicePoint: item?.customFields?.servicePoint || '-',
      reason: ARNM_REASONS_MAP[item?.customFields?.reason] || '-',
      outDateFromServicePoint: item?.customFields?.outDateFromServicePoint
        ? formatDate(item?.customFields?.outDateFromServicePoint, 'dd/MM/yyyy')
        : '-',
      dateToServicePoint: item?.customFields?.dateToServicePoint
        ? formatDate(
            item?.customFields?.dateToServicePoint,
            'dd/MM/yyyy h:mm aaa'
          )
        : '-',
      expectedOutFromServicePoint: item?.customFields
        ?.expectedOutFromServicePoint
        ? formatDate(
            item?.customFields?.expectedOutFromServicePoint,
            'dd/MM/yyyy'
          )
        : '-',
      title: item?.customFields?.vehiclePlate || '',
      vehicleDescription: item?.customFields?.vehicleDescription || '',
    };
  });

  return tasks;
};

export const getArnmMaintenanceColumns = (tasks = []) => {
  const order = ARNM_MAINTENANCE_TASK_STATUSES_ORDER;
  const groupedByStatus = groupBy(tasks, 'status');

  const columns = order.reduce((acc, cur) => {
    acc.push({
      id: cur,
      title: ARNM_MAINTENANCE_TASK_STATUSES_NAMES[cur].label,
      cards: groupedByStatus[cur] || [],
      styles: ARNM_MAINTENANCE_TASK_STATUSES_NAMES[cur].styles,
      value: true,
      label: ARNM_MAINTENANCE_TASK_STATUSES_NAMES[cur].label,
      key: cur,
    });
    return acc;
  }, []);

  return columns;
};

export const getArnmMaintenancePayload = (
  payload = {},
  modalFiltersRules = null
) => {
  const fields =
    omit(payload, [
      'page',
      'pageSize',
      'order',
      'orderBy',
      'boardType',
      'query',
    ]) || {};
  const fieldsMap = {
    reason: 'customFields.reason',
    servicePoint: 'customFields.servicePoint',
    stage: 'status',
  };

  const multyFilterStringFields = ['reason', 'servicePoint'];
  const multyFilterNumberFields = [
    'stage',
    'reservationType',
    'procurementStageId',
  ];
  const defaultRule = {
    field: 'boardId',
    operator: FILTER_OPERATOR_ENUMS['='],
    type: FILTER_RULE_ENUMS.ComparableRuleType,
    value: BOARDS_ID.ARM_MAINTENANCE,
  };

  const getValueField = ({ field }) => {
    if (field === 'assignee') {
      return payload[field] === 'none' ? '' : payload[field];
    }
    if (multyFilterStringFields.includes(field)) {
      return payload[field]?.split(',');
    }
    if (multyFilterNumberFields.includes(field)) {
      return payload[field]?.split(',').map((item) => Number(item));
    }
    return payload[field];
  };
  const getType = ({ field }) => {
    if (
      multyFilterStringFields.includes(field) ||
      multyFilterNumberFields.includes(field)
    ) {
      return FILTER_RULE_ENUMS.IsAnyOfRuleType;
    }
    return FILTER_RULE_ENUMS.ComparableRuleType;
  };
  const rules = [...Object.keys(fields)].map((field) => ({
    field: fieldsMap[field] || field,
    operator: FILTER_OPERATOR_ENUMS['='],
    type: getType({ field }),
    value: getValueField({ field }),
  }));
  rules.push(...(modalFiltersRules?.value || []));
  rules.push(defaultRule);

  return {
    type: 0,
    field: null,
    operator: null,
    value: rules,
  };
};

export const normalizeOrderBy = (orderBy) => {
  switch (orderBy) {
    case 'dateToServicePoint':
      return `customFields.${orderBy}`;
    default:
      return orderBy;
  }
};

export const getArnmMaintenanceTaskPayload = (values) => {
  const clearableFields = ['dot', 'tires'];

  Object.keys(values)?.forEach((key) => {
    if (values[key] === '' && !clearableFields.includes(key)) {
      values[key] = null;
    }
  });

  const cleanedValues = omit(values, [
    'assignee',
    'documents',
    'tags',
    'status',
    'updatedAt',
    'updatedBy',
    'changelog',
    'images',
    'parent',
    'name',
    'createdAt',
    'comments',
    'createdBy',
    'servicePoint',
    'subscriptionId',
    'customFields.vehicleDescription',
    'customFields.vehicleImage',
    'customFields.vehicleOwner',
    'customFields.vehiclePlate',
  ]);

  return {
    assignee: values.assignee?.value,
    customFields: {
      ...cleanedValues.customFields,
      servicePoint: values.customFields.servicePoint?.value,
      expectedOutFromServicePoint:
        values.customFields.expectedOutFromServicePoint || null,
    },
  };
};
